import { parseBookingTime } from "./booking-time";

type Selection = {
  rentalType: "driving" | "chauffeured" | "event";
  dateFrom: string; dateTo: string; pickupTime: string; dropoffTime: string;
  timezone?: string | null; advanceNoticeDays?: number; now?: Date;
  /**
   * Explicit chauffeur/event duration (hours). When supplied, the end was
   * derived as start + duration (may cross midnight, up to a 24h full day),
   * so the legacy same-day window rule does not apply. Legacy callers that
   * omit it keep the same-day validation unchanged.
   */
  durationHours?: number;
};
export type TimeIssue = {
  reason: "invalid_date_format" | "invalid_time_format" | "end_before_start" |
    "chauffeured_same_day_window" | "minimum_hourly_duration" | "minimum_notice";
  correction?: { dateTo: string; dropoffTime: string };
  earliestDate?: string;
};
const day = (date: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return NaN;
  const value = Date.parse(`${date}T00:00:00Z`);
  return Number.isFinite(value) && new Date(value).toISOString().slice(0, 10) === date ? value : NaN;
};
const minutes = (value: string) => {
  const parsed = parseBookingTime(value);
  return parsed ? parsed.hour24 * 60 + parsed.minute : NaN;
};
export function checkoutTimeIssue(input: Selection): TimeIssue | null {
  if (!input.dateFrom || !input.dateTo) return null;
  const from = day(input.dateFrom), to = day(input.dateTo);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return { reason: "invalid_date_format" };
  const pickup = minutes(input.pickupTime), dropoff = minutes(input.dropoffTime);
  if (!Number.isFinite(pickup) || !Number.isFinite(dropoff)) return { reason: "invalid_time_format" };
  // Work in selected local calendar/clock units, never the browser's timezone.
  // Physical pickup deadlines remain server-owned.
  if (input.timezone && (input.advanceNoticeDays ?? 0) > 0) {
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: input.timezone,
      year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(input.now ?? new Date());
    const part = (type: string) => parts.find(value => value.type === type)!.value;
    const today = `${part("year")}-${part("month")}-${part("day")}`;
    const earliestDate = new Date(day(today) + input.advanceNoticeDays! * 86400000).toISOString().slice(0, 10);
    if (input.dateFrom < earliestDate) return { reason: "minimum_notice", earliestDate };
  }
  if (input.rentalType !== "driving" && input.durationHours !== undefined) {
    const h = input.durationHours;
    if (!Number.isFinite(h) || h < 1 || h > 24) return { reason: "minimum_hourly_duration" };
    // Local wall-clock arithmetic (same as server pricing): the end must be
    // after the start and exactly start + duration.
    const elapsedMin = (to + dropoff * 60000 - (from + pickup * 60000)) / 60000;
    if (elapsedMin <= 0) return { reason: "end_before_start" };
    return Math.abs(elapsedMin - h * 60) < 1e-6 ? null : { reason: "minimum_hourly_duration" };
  }
  const start = from + pickup * 60000;
  const end = input.rentalType === "chauffeured" ? from + dropoff * 60000 : to + dropoff * 60000;
  const minimum = input.rentalType === "driving" ? 1 : 60;
  if (end - start >= minimum * 60000 && to >= from) return null;
  const reason = to < from || end <= start
    ? input.rentalType === "chauffeured" ? "chauffeured_same_day_window" : "end_before_start"
    : "minimum_hourly_duration";
  let corrected = start + (input.rentalType === "driving" ? 24 * 60 : 60) * 60000;
  // An event ending at an earlier clock time can be an overnight selection.
  if (input.rentalType === "event" && from === to && dropoff < pickup) {
    corrected = Math.max(to + 86400000 + dropoff * 60000, corrected);
  }
  if (input.rentalType === "chauffeured" && corrected >= from + 86400000) return { reason };
  const correctedDate = new Date(corrected).toISOString();
  return { reason, correction: { dateTo: correctedDate.slice(0, 10), dropoffTime: correctedDate.slice(11, 16) } };
}

export function checkoutSelectionProperties(input: Selection) {
  const stamp = (date: string, clock: string) => {
    const value = minutes(clock);
    return Number.isFinite(day(date)) && Number.isFinite(value)
      ? `${date}T${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}:00` : null;
  };
  return { selected_start: stamp(input.dateFrom, input.pickupTime),
    selected_end: stamp(input.dateTo, input.dropoffTime),
    start_date: input.dateFrom || null, end_date: input.dateTo || null,
    start_time: input.pickupTime || null, end_time: input.dropoffTime || null,
    timezone: input.timezone || null, timezone_source: input.timezone ? "car_location_estimate" : "unknown" };
}
